import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Clock, Globe, Loader2, LogIn, Lock, User } from "lucide-react";
import { formatAdminDateTime } from "../utils/adminDateTime";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "digsby_admin_token";

function fmt(iso) {
  return formatAdminDateTime(iso);
}

function minsLeft(iso) {
  try {
    const diff = (new Date(iso).getTime() - Date.now()) / 60000;
    return diff > 0 ? Math.ceil(diff) : 0;
  } catch { return 0; }
}

export default function LoginActivity({ onAuthError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem(TOKEN_KEY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/login-activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="rounded-3xl border-2 border-black/10 bg-white p-6 mb-8 flex items-center gap-2 text-slate-500" data-testid="login-activity-loading">
        <Loader2 size={18} className="animate-spin" /> Loading login activity…
      </div>
    );
  }
  if (!data) return null;

  const failures = data.recent_failures || [];
  const locks = data.active_locks || [];

  return (
    <div className="rounded-3xl border-2 border-black/10 bg-white p-6 mb-8" data-testid="login-activity-panel">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck size={20} className="text-[#0000FF]" />
        <h2 className="font-heading font-bold text-xl">Login Activity</h2>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 mb-5">
        <div className="rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-4" data-testid="login-activity-last">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><LogIn size={13} /> This session</p>
          <p className="font-heading font-bold text-lg mt-1 flex items-center gap-2"><Clock size={16} className="text-[#00A000]" /> {fmt(data.last_login?.at)}</p>
          {data.last_login?.email && <p className="text-sm font-bold text-[#0000FF] flex items-center gap-1.5 mt-1" data-testid="login-activity-last-email"><User size={13} /> {data.last_login.email}</p>}
          <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Globe size={13} /> {data.last_login?.ip || "-"}</p>
        </div>
        <div className="rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-4" data-testid="login-activity-previous">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><Clock size={13} /> Previous login</p>
          <p className="font-heading font-bold text-lg mt-1">{data.previous_login ? fmt(data.previous_login.at) : "No earlier login recorded"}</p>
          {data.previous_login?.email && <p className="text-sm font-bold text-[#0000FF] flex items-center gap-1.5 mt-1" data-testid="login-activity-previous-email"><User size={13} /> {data.previous_login.email}</p>}
          {data.previous_login && <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Globe size={13} /> {data.previous_login.ip}</p>}
        </div>
      </div>

      {locks.length > 0 && (
        <div className="rounded-2xl border-2 border-[#FF0000]/30 bg-[#FF0000]/5 p-4 mb-5" data-testid="login-activity-locks">
          <p className="font-bold text-[#FF0000] flex items-center gap-2 mb-2"><Lock size={16} /> Currently locked out ({locks.length})</p>
          <ul className="space-y-1 text-sm">
            {locks.map((l, i) => (
              <li key={i} className="text-slate-700" data-testid={`login-activity-lock-${i}`}>
                <span className="font-mono">{l.ip}</span> · {l.email || "unknown"} · unlocks in {minsLeft(l.locked_until)} min
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="text-sm font-bold text-slate-600 mb-3 flex items-center gap-1.5">
          <ShieldAlert size={15} className="text-[#FF0000]" /> Recent failed attempts
        </p>
        {failures.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="login-activity-no-failures">No failed login attempts recorded. 👍</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border-2 border-black/10" data-testid="login-activity-events">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#FFFBF2] text-slate-500 uppercase text-xs tracking-wide">
                <tr>
                  <th className="p-3">When</th>
                  <th className="p-3">Email tried</th>
                  <th className="p-3">IP</th>
                  <th className="p-3">Type</th>
                </tr>
              </thead>
              <tbody>
                {failures.map((e, i) => (
                  <tr key={i} className="border-t border-black/5" data-testid={`login-activity-event-${i}`}>
                    <td className="p-3 whitespace-nowrap">{fmt(e.at)}</td>
                    <td className="p-3">{e.email || "-"}</td>
                    <td className="p-3 font-mono text-slate-600">{e.ip || "-"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${e.type === "lockout" ? "bg-[#FF0000]/10 text-[#FF0000]" : "bg-[#FBEF14]/60 text-slate-700"}`}>
                        {e.type === "lockout" ? "Lockout" : "Failed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
